import { type IOutboxItem, type ISupportMessage } from '../interface/support-chat';

// localStorage persistence for the support-chat outbox, modelled on pu-events-store.ts.
// Every access is try/catch-wrapped: when storage is unavailable the feature degrades to
// online-only in-session state rather than throwing on a field agent's phone.
//
// Keyed by userId, not by thread. Agents share phones at collation centres, and one
// agent's queued message must never flush under the next agent's token.

const OUTBOX_KEY = (userId: number) => `support-chat:v1:outbox:${userId}`;
// Per user AND per thread: a shared phone must not paint one agent's transcript for another.
// Keys written under the old per-thread shape simply go unread.
const CACHE_KEY = (userId: number, threadId: string) => `support-chat:v1:cache:${userId}:${threadId}`;
const READ_KEY = (userId: number, threadId: string) => `support-chat:v1:read:${userId}:${threadId}`;

/**
 * Same-tab change notification.
 *
 * The FAB badge, the Help hub row and the open chat panel are three subscribers in ONE
 * tab, and the native `storage` event only fires across tabs. Every write dispatches this
 * so all three re-read. Same reason pu-events-store carries `pu-events:changed`.
 */
const CHANGED_EVENT = 'support-chat:changed';

/** Messages retained per thread for instant paint before the live fetch lands. */
const CACHE_LIMIT = 50;

/**
 * Refuse to grow the payload past this. localStorage is ~5MB and shared with drafts, the
 * theme and every pu-events queue; filling it would evict them. We shed cache before we
 * ever refuse a queued message — see `writeOutbox`.
 */
const MAX_PAYLOAD_BYTES = 2_000_000;

export class OutboxStorageFullError extends Error {
	constructor() {
		super('support-chat outbox is full');
		this.name = 'OutboxStorageFullError';
	}
}

const notifyChanged = (userId: number) => {
	try {
		window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { userId } }));
	} catch {
		// Non-browser environment (tests) — nothing to notify.
	}
};

export const subscribeSupportChatChanged = (userId: number, onChange: () => void): (() => void) => {
	const handler = (event: Event) => {
		const detail = (event as CustomEvent<{ userId: number }>).detail;
		if (detail?.userId === userId) onChange();
	};
	try {
		window.addEventListener(CHANGED_EVENT, handler);
	} catch {
		return () => undefined;
	}
	return () => window.removeEventListener(CHANGED_EVENT, handler);
};

export const readOutbox = (userId: number): IOutboxItem[] => {
	try {
		const raw = localStorage.getItem(OUTBOX_KEY(userId));
		const parsed = raw ? (JSON.parse(raw) as IOutboxItem[]) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

/**
 * Persist the queue.
 *
 * Throws {@link OutboxStorageFullError} when the payload will not fit — deliberately, so
 * the composer can tell the agent their phone is out of space. Silently dropping a queued
 * message about a blocked polling unit is the one failure this module must never have.
 */
export const writeOutbox = (userId: number, queue: IOutboxItem[]) => {
	const payload = JSON.stringify(queue);
	if (payload.length > MAX_PAYLOAD_BYTES) {
		throw new OutboxStorageFullError();
	}
	try {
		localStorage.setItem(OUTBOX_KEY(userId), payload);
	} catch {
		// Quota or private-mode failure. The in-session queue still covers this run; the
		// agent loses only the ability to survive a reload, and the composer surfaces it.
		throw new OutboxStorageFullError();
	}
	notifyChanged(userId);
};

/** Insert or replace by clientId — an item is never duplicated by a retry or a re-render. */
export const upsertOutboxItem = (userId: number, item: IOutboxItem): IOutboxItem[] => {
	const queue = readOutbox(userId);
	const next = queue.some((i) => i.clientId === item.clientId)
		? queue.map((i) => (i.clientId === item.clientId ? item : i))
		: [...queue, item];
	writeOutbox(userId, next);
	return next;
};

export const removeOutboxItem = (userId: number, clientId: string): IOutboxItem[] => {
	const next = readOutbox(userId).filter((i) => i.clientId !== clientId);
	writeOutbox(userId, next);
	return next;
};

/**
 * Oldest first, so a burst composed offline arrives in the order it was written.
 *
 * Sorting on createdAt is safe here in a way it would not be for the transcript: these are
 * all this device's own messages, stamped by one clock.
 */
export const pendingItems = (userId: number, threadRef?: string): IOutboxItem[] =>
	itemsForThread(userId, threadRef)
		.filter((i) => i.state !== 'held')
		.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

/** The local thread id an item belongs to. Items written before the field existed are support's. */
export const LEGACY_THREAD_REF = 'support';

export const threadRefOf = (item: Pick<IOutboxItem, 'threadRef'>): string => item.threadRef ?? LEGACY_THREAD_REF;

/**
 * One conversation's slice of the per-user queue.
 *
 * <p>The queue stays keyed by USER (shared phones), but each conversation engine must drain
 * only its own items: a message queued for a polling-unit agent must never be delivered to the
 * support desk because the desk's thread happened to resolve first. `threadRef` omitted means
 * every item, which only the tests and the storage-pressure path want.</p>
 */
export const itemsForThread = (userId: number, threadRef?: string): IOutboxItem[] => {
	const all = readOutbox(userId);
	if (threadRef === undefined) return all;
	return all.filter((i) => threadRefOf(i) === threadRef);
};

/**
 * Whether a queued item is waiting for its photo to be picked again.
 *
 * <p>True when the item carries attachment metadata but its File is no longer in memory —
 * which happens on any tab reload, because the blob is deliberately never persisted (~5MB
 * of localStorage, and a 3MB photo base64s to ~4MB, which would evict the queued TEXT
 * messages alongside it).</p>
 *
 * <p>Such an item must be SKIPPED by the flush, never sent. Sending it would deliver a
 * message the agent believes has a photo attached and which silently does not — worse than
 * not sending at all, because they would never find out.</p>
 */
export const needsReattachment = (item: Pick<IOutboxItem, 'attachment'>, hasFile: boolean): boolean =>
	!!item.attachment && !hasFile;

export const heldItems = (userId: number, threadRef?: string): IOutboxItem[] =>
	itemsForThread(userId, threadRef).filter((i) => i.state === 'held');

/** Last messages for a thread, for instant paint. Trimmed to the newest CACHE_LIMIT. */
export const readThreadCache = (userId: number, threadId: string): ISupportMessage[] => {
	try {
		const raw = localStorage.getItem(CACHE_KEY(userId, threadId));
		const parsed = raw ? (JSON.parse(raw) as ISupportMessage[]) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

export const writeThreadCache = (userId: number, threadId: string, messages: ISupportMessage[]) => {
	try {
		localStorage.setItem(CACHE_KEY(userId, threadId), JSON.stringify(messages.slice(-CACHE_LIMIT)));
	} catch {
		// Cache is an optimisation. Losing it costs a blank frame, never a message —
		// so unlike writeOutbox this one stays silent.
	}
};

/** Frees space for the queue when storage is tight. Cache is the first thing to go. */
export const clearThreadCache = (userId: number, threadId: string) => {
	try {
		localStorage.removeItem(CACHE_KEY(userId, threadId));
	} catch {
		// Nothing to free.
	}
};

export interface IReadMarks {
	/** Highest sequence this device has reported as read. */
	mine: number;
	/** Highest sequence the desk has read of ours — drives the single `Read 12:04` line. */
	theirs: number;
}

export const readMarks = (userId: number, threadId: string): IReadMarks => {
	try {
		const raw = localStorage.getItem(READ_KEY(userId, threadId));
		const parsed = raw ? (JSON.parse(raw) as Partial<IReadMarks>) : {};
		return { mine: parsed.mine ?? 0, theirs: parsed.theirs ?? 0 };
	} catch {
		return { mine: 0, theirs: 0 };
	}
};

export const writeReadMarks = (userId: number, threadId: string, marks: IReadMarks) => {
	try {
		localStorage.setItem(READ_KEY(userId, threadId), JSON.stringify(marks));
	} catch {
		// Read state re-syncs from the server on reconnect.
	}
};
